import { ReactNode, useEffect, useRef } from "react"
import { useMutation } from "@apollo/client"
import { EstadoPagoOrdenes, TipoOrdenPanelCocina, useListenCheckOrdenesSubscription } from "src/gql/schema"
import { useProfile } from "../hooks/useProfile"
import { usePrintTicket } from "../hooks/print"
import { RoleNames } from "../hooks/useAuth"
import { RECLAMAR_IMPRESION_ORDEN } from "src/graphql/mutations/impresion-orden"

const NEW_ORDER_AUDIO_URL = "https://articulos.nyc3.cdn.digitaloceanspaces.com/easyroom/pedido%20easyroom%20grave.mp3"

// Usuarios con los que llegan las ordenes de Scandify
export const USUARIOS_ORDEN_SCANDIFY = ["e2921491-dfa2-4615-b9f3-4a9129dd7450", "ffb2168b-a25d-4f48-b1ef-5b088e6bdff3"]

// Recepcion y room service reciben el aviso de orden nueva en cualquier pantalla
export const ROLES_ALERTA_ORDEN_NUEVA: string[] = [RoleNames.recepcionista, RoleNames.roomService]

// El navegador no deja reproducir audio hasta que el usuario interactua con la pagina,
// por eso se reproduce en silencio y se pausa en el primer click o tecla
export const useNewOrderAudio = (enabled = true) => {
    const newOrderAudio = useRef(new Audio(NEW_ORDER_AUDIO_URL))
    const audioUnlocked = useRef(false)

    useEffect(() => {
        if (!enabled) return
        const audio = newOrderAudio.current
        audio.load()
        const events = ["click", "keydown"] as const
        const unlock = () => {
            if (audioUnlocked.current) return
            // Sin muted se alcanza a escuchar un pedazo del audio antes del pause
            audio.muted = true
            audio
                .play()
                .then(() => {
                    audio.pause()
                    audio.currentTime = 0
                    audioUnlocked.current = true
                })
                .catch(() => undefined)
                .finally(() => {
                    audio.muted = false
                })
            events.forEach((e) => document.removeEventListener(e, unlock))
        }
        events.forEach((e) => document.addEventListener(e, unlock))
        return () => events.forEach((e) => document.removeEventListener(e, unlock))
    }, [enabled])

    return newOrderAudio
}

const AlertaOrdenNuevaProvider = ({ children }: { children: ReactNode }) => {
    const { hotel_id, rolName } = useProfile()
    const { handlePrint } = usePrintTicket("snackbar-mini")
    const [reclamarImpresion] = useMutation<{ reclamar_impresion_orden: boolean }>(RECLAMAR_IMPRESION_ORDEN)
    // Ordenes ya avisadas, fuera del panel de cocina no esta la lista para saber si es repetida
    const ordenesAvisadas = useRef(new Set<string>())

    const recibeAlerta = ROLES_ALERTA_ORDEN_NUEVA.includes(rolName)
    const newOrderAudio = useNewOrderAudio(recibeAlerta)

    const { data } = useListenCheckOrdenesSubscription({
        variables: { hotel_id },
        skip: !recibeAlerta || !hotel_id,
    })

    useEffect(() => {
        const ordenEnPreparacion = data?.checkOrdenes?.orden_en_preparacion
        if (ordenEnPreparacion?.accion !== "agregar") return

        const newOrder = ordenEnPreparacion.orden
        if (!USUARIOS_ORDEN_SCANDIFY.includes(newOrder?.usuario_id || "")) return

        const key =
            ordenEnPreparacion.tipo === TipoOrdenPanelCocina.Comanda ? newOrder?.virtual_comanda_id : newOrder?.orden_id
        if (!key || ordenesAvisadas.current.has(key)) return
        ordenesAvisadas.current.add(key)

        // Si ya esta pagada se imprime el ticket del pago con el tipo 1, si no la orden con el tipo 3
        const ticketPagado = newOrder?.estado_pago === EstadoPagoOrdenes.Pagada ? newOrder?.pago?.ticket_id : null
        // El print:// navega la ventana y corta el audio, por eso se imprime hasta que termina
        const imprimir = () =>
            ticketPagado
                ? handlePrint(ticketPagado, "custom", "1")
                : handlePrint(newOrder?.virtual_comanda_id || newOrder?.orden_id, "custom", "3")
        // Todas las sesiones de recepcion y room service reciben la orden, solo imprime la que la reclama primero.
        // Si falla la mutacion se imprime de todos modos para no perder el ticket
        const imprimePorReclamo = reclamarImpresion({ variables: { hotel_id, orden_key: key } })
            .then(({ data }) => !!data?.reclamar_impresion_orden)
            .catch(() => true)
        const imprimirSiReclamo = () =>
            imprimePorReclamo.then((imprime) => {
                if (imprime) imprimir()
            })

        const audio = newOrderAudio.current
        audio.currentTime = 0
        audio
            .play()
            .then(() => audio.addEventListener("ended", imprimirSiReclamo, { once: true }))
            .catch(() => imprimirSiReclamo())
    }, [data])

    return <>{children}</>
}

export default AlertaOrdenNuevaProvider
